// Etkinlik penceresi (görüntüle / ekle / düzenle / sil) — Blazor karşılığı: DxScheduler'ın
// AppointmentFormMode="EditForm" formu + HomePage.razor.cs AppointmentInserting/Updating/Removing.
// Yalnızca Local kaynağa ait etkinlikler yazılabilir; diğer kaynak tipleri salt okunur gösterilir.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, MapPin, Trash2 } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { Input } from '@/shared/ui/Input';
import { Switch } from '@/shared/ui/Switch';
import { Textarea } from '@/shared/ui/Textarea';
import { dialogs } from '@/shared/ui/dialogStore';
import { toDateTimeString } from '@/shared/lib/octo/dateValue';
import {
  dateKey,
  formatDayLabel,
  formatEventTime,
  formatHourMinute,
  weekdayName,
} from '../model/calendarModel';
import { CALENDAR_SOURCE_TYPE } from '../model/calendarTypes';
import type { CalendarEvent, CalendarSource, LocalCalendarEventInput } from '../model/calendarTypes';

export interface CalendarEventDraft {
  start: Date;
  end: Date;
  allDay: boolean;
  sourceId?: string | null;
}

export interface CalendarEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Var olan etkinlik; null ise yeni kayıt açılır. */
  event: CalendarEvent | null;
  /** Yeni kayıt için ön değerler (boş hücreye tıklama). */
  draft?: CalendarEventDraft | null;
  sources: CalendarSource[];
  userId: string;
  onSave: (input: LocalCalendarEventInput) => Promise<void>;
  onDelete: (eventId: string) => Promise<void>;
  saving?: boolean;
}

const dateInputValue = (d: Date) => dateKey(d);
const timeInputValue = (d: Date) => formatHourMinute(d);

function combine(dateStr: string, timeStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = (timeStr || '00:00').split(':').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0);
}

export function CalendarEventDialog(props: CalendarEventDialogProps) {
  const { open, onOpenChange, event, draft } = props;
  const { t } = useTranslation();
  const title = event
    ? event.readOnly
      ? t('calendar.event.title')
      : t('calendar.event.editTitle')
    : t('calendar.event.newTitle');

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} size="md">
      {event?.readOnly ? (
        <ReadOnlyEvent event={event} />
      ) : (
        <EventForm key={event?.id ?? (draft ? `draft-${draft.start.getTime()}` : 'new')} {...props} />
      )}
    </Dialog>
  );
}

function ReadOnlyEvent({ event }: { event: CalendarEvent }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <span
          className="mt-1.5 h-3 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: event.color }}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <div className="text-fg text-base font-semibold">{event.title}</div>
          <div className="text-fg-muted text-sm">{event.sourceName}</div>
        </div>
      </div>
      <div className="text-fg-muted flex items-center gap-2 text-sm">
        <CalendarClock className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          {formatDayLabel(event.start)}, {weekdayName(event.start)} ·{' '}
          {formatEventTime(event, t('calendar.allDay'))}
        </span>
      </div>
      {event.location ? (
        <div className="text-fg-muted flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{event.location}</span>
        </div>
      ) : null}
      {event.description ? <p className="text-fg text-sm whitespace-pre-wrap">{event.description}</p> : null}
      <p className="text-fg-subtle text-xs">{t('calendar.event.readOnlyHint')}</p>
    </div>
  );
}

function EventForm({
  event,
  draft,
  sources,
  userId,
  onSave,
  onDelete,
  onOpenChange,
  saving,
}: CalendarEventDialogProps) {
  const { t } = useTranslation();
  const writableSources = sources.filter((s) => s.sourceType === CALENDAR_SOURCE_TYPE.Local && !s.isReadOnly);

  const initialStart = event?.start ?? draft?.start ?? new Date();
  const initialEnd = event?.end ?? draft?.end ?? new Date(initialStart.getTime() + 60 * 60_000);

  const [subject, setSubject] = useState(event?.title && event.title !== '(Başlıksız)' ? event.title : '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [location, setLocation] = useState(event?.location ?? '');
  const [allDay, setAllDay] = useState(event?.allDay ?? draft?.allDay ?? false);
  const [startDate, setStartDate] = useState(dateInputValue(initialStart));
  const [startTime, setStartTime] = useState(timeInputValue(initialStart));
  const [endDate, setEndDate] = useState(dateInputValue(initialEnd));
  const [endTime, setEndTime] = useState(timeInputValue(initialEnd));
  const [sourceId, setSourceId] = useState(
    event?.sourceId ?? draft?.sourceId ?? writableSources[0]?.calendarSourceId ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!subject.trim()) {
      setError(t('calendar.event.subjectRequired'));
      return;
    }
    const start = allDay ? combine(startDate, '00:00') : combine(startDate, startTime);
    let end = allDay ? combine(endDate, '00:00') : combine(endDate, endTime);
    if (end.getTime() < start.getTime()) end = new Date(start.getTime() + (allDay ? 0 : 30 * 60_000));

    setError(null);
    await onSave({
      calendarEventId: event?.id,
      userId,
      subject: subject.trim(),
      description: description.trim(),
      location: location.trim(),
      start: toDateTimeString(start),
      end: toDateTimeString(end),
      allDay,
      labelId: 0,
      statusId: 0,
      recurrenceInfo: null,
      calendarSourceId: sourceId || null,
      addUserId: userId,
      updateUserId: userId,
    });
    onOpenChange(false);
  };

  const remove = async () => {
    if (!event) return;
    const ok = await dialogs.confirm({ message: t('calendar.event.deleteConfirm'), kind: 'delete' });
    if (!ok) return;
    await onDelete(event.id);
    onOpenChange(false);
  };

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-fg-muted mb-1 block text-xs font-medium">{t('calendar.event.subject')}</span>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={t('calendar.event.subjectPlaceholder')}
          autoFocus
        />
      </label>

      <div className="flex items-center justify-between">
        <span className="text-fg text-sm">{t('calendar.allDay')}</span>
        <Switch checked={allDay} onCheckedChange={setAllDay} aria-label={t('calendar.allDay')} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-fg-muted mb-1 block text-xs font-medium">{t('calendar.event.start')}</span>
          <div className="flex gap-2">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            {allDay ? null : (
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-28"
              />
            )}
          </div>
        </label>
        <label className="block">
          <span className="text-fg-muted mb-1 block text-xs font-medium">{t('calendar.event.end')}</span>
          <div className="flex gap-2">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            {allDay ? null : (
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-28"
              />
            )}
          </div>
        </label>
      </div>

      <label className="block">
        <span className="text-fg-muted mb-1 block text-xs font-medium">{t('calendar.event.location')}</span>
        <Input value={location} onChange={(e) => setLocation(e.target.value)} />
      </label>

      <label className="block">
        <span className="text-fg-muted mb-1 block text-xs font-medium">
          {t('calendar.event.description')}
        </span>
        <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      {writableSources.length > 1 ? (
        <label className="block">
          <span className="text-fg-muted mb-1 block text-xs font-medium">{t('calendar.event.source')}</span>
          <select
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            className="border-border bg-bg-elevated text-fg h-[var(--control-h)] w-full rounded-md border px-3 text-sm"
          >
            {writableSources.map((s) => (
              <option key={s.calendarSourceId} value={s.calendarSourceId}>
                {s.sourceName}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {error ? <p className="text-danger text-sm">{error}</p> : null}

      <div className="flex items-center justify-between gap-2 pt-1">
        {event ? (
          <Button variant="ghost" className="text-danger" onClick={() => void remove()} disabled={saving}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {t('calendar.event.delete')}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('calendar.event.cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            {t('calendar.event.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
